import type { SupportTicketStatus } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import {
  encodeSupportTicketFilterGroup,
  supportTicketFilterData,
  SupportTicketFilterNames,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import type {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import type { ArrayStringFilterData } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import {
  FilterOperator,
  isFilterGroup,
} from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import {
  AI_CONVERSATIONS_VIEW_FILTER_GROUP,
  ALL_VIEW_FILTER_GROUP,
  ARCHIVED_VIEW_FILTER_GROUP,
  buildAssignedViewFilterGroup,
  buildMentionsViewFilterGroup,
  DefaultView,
  DRAFTS_VIEW_FILTER_GROUP,
  OPEN_VIEW_FILTER_GROUP,
  SENT_VIEW_FILTER_GROUP,
  SPAM_VIEW_FILTER_GROUP,
  withCustomViewDefaults,
  withInitialViewDefaults,
} from "./default-views";

export interface SupportTicketCountView {
  viewKey: string;
  filterGroup: string | null;
}

export interface SupportTicketCountSourceView {
  id: string | number;
  filterGroup: FilterGroup | null;
}

export type CountStatusValue = Extract<
  SupportTicketStatus,
  "OPEN" | "CLOSED" | "IN_PROGRESS" | "SNOOZED" | "ARCHIVED" | "DRAFT"
>;

export const COUNT_STATUS_VALUES: CountStatusValue[] = [
  "OPEN",
  "CLOSED",
  "IN_PROGRESS",
  "SNOOZED",
  "ARCHIVED",
  "DRAFT",
];

/**
 * Status values to include in the "All" count.
 * HIDDEN is not included because it's not a user-facing status in the filter toolbar.
 */
export const ALL_COUNT_STATUS_VALUES: CountStatusValue[] = [
  "OPEN",
  "CLOSED",
  "IN_PROGRESS",
  "SNOOZED",
  "ARCHIVED",
  "DRAFT",
];

export function encodeSupportTicketCountFilterGroup(
  filterGroup: FilterGroup | null,
): string | null {
  if (!filterGroup) {
    return null;
  }
  try {
    return encodeSupportTicketFilterGroup(filterGroup);
  } catch {
    return null;
  }
}

/**
 * Adds the active words search to a view's own filter group, keeping search
 * filters separate from view filters so a count can evaluate the search inside
 * a view's scope without redefining the view.
 */
export function withSupportTicketSearchFilters(
  filterGroup: FilterGroup | null,
  searchFilters: readonly AdvancedFilterData[],
): FilterGroup | null {
  if (searchFilters.length === 0) {
    return filterGroup;
  }

  if (!filterGroup) {
    return { operator: FilterOperator.AND, clauses: [...searchFilters] };
  }

  if (filterGroup.operator === FilterOperator.AND) {
    return {
      ...filterGroup,
      clauses: [...filterGroup.clauses, ...searchFilters],
    };
  }

  return {
    operator: FilterOperator.AND,
    clauses: [filterGroup, ...searchFilters],
  };
}

export function buildDefaultSupportTicketCountViews(
  userId: string | null,
): SupportTicketCountView[] {
  const views: { viewKey: DefaultView; filterGroup: FilterGroup }[] = [
    { viewKey: DefaultView.ALL, filterGroup: ALL_VIEW_FILTER_GROUP },
    { viewKey: DefaultView.OPEN, filterGroup: OPEN_VIEW_FILTER_GROUP },
    { viewKey: DefaultView.DRAFTS, filterGroup: DRAFTS_VIEW_FILTER_GROUP },
    {
      viewKey: DefaultView.AI_CONVERSATIONS,
      filterGroup: AI_CONVERSATIONS_VIEW_FILTER_GROUP,
    },
    { viewKey: DefaultView.SENT, filterGroup: SENT_VIEW_FILTER_GROUP },
    { viewKey: DefaultView.SPAM, filterGroup: SPAM_VIEW_FILTER_GROUP },
    { viewKey: DefaultView.ARCHIVED, filterGroup: ARCHIVED_VIEW_FILTER_GROUP },
  ];

  if (userId) {
    views.push(
      {
        viewKey: DefaultView.ASSIGNED,
        filterGroup: buildAssignedViewFilterGroup(userId),
      },
      {
        viewKey: DefaultView.MENTIONS,
        filterGroup: buildMentionsViewFilterGroup(userId),
      },
    );
  }

  return views.map(({ viewKey, filterGroup }) => ({
    viewKey,
    filterGroup: encodeSupportTicketCountFilterGroup(
      withInitialViewDefaults(viewKey, filterGroup),
    ),
  }));
}

export function buildCustomSupportTicketCountViews(
  views: readonly SupportTicketCountSourceView[],
): SupportTicketCountView[] {
  return views.map((view) => ({
    viewKey: String(view.id),
    filterGroup: encodeSupportTicketCountFilterGroup(
      withCustomViewDefaults(view.filterGroup),
    ),
  }));
}

export function getBaseFilterGroupWithoutStatus(
  filterGroup: FilterGroup,
): FilterGroup | null {
  const clauses: FilterGroup["clauses"] = [];

  for (const clause of filterGroup.clauses) {
    if (isFilterGroup(clause)) {
      const childGroup = getBaseFilterGroupWithoutStatus(clause);
      if (childGroup) {
        clauses.push(childGroup);
      }
    } else if (clause.name !== SupportTicketFilterNames.STATUS) {
      clauses.push(clause);
    }
  }

  return clauses.length === 0 ? null : { ...filterGroup, clauses };
}

export function buildStatusVariantFilterGroup(
  baseFilterGroup: FilterGroup | null,
  status: CountStatusValue,
): FilterGroup {
  const statusFilter: ArrayStringFilterData = {
    ...(supportTicketFilterData[
      SupportTicketFilterNames.STATUS
    ] as ArrayStringFilterData),
    value: [status],
  };

  if (!baseFilterGroup) {
    return { operator: FilterOperator.AND, clauses: [statusFilter] };
  }

  if (baseFilterGroup.operator === FilterOperator.AND) {
    return {
      ...baseFilterGroup,
      clauses: [...baseFilterGroup.clauses, statusFilter],
    };
  }

  return {
    operator: FilterOperator.AND,
    clauses: [baseFilterGroup, statusFilter],
  };
}

export function buildStatusCountViews(
  filterGroup: FilterGroup,
): SupportTicketCountView[] {
  const baseFilterGroup = getBaseFilterGroupWithoutStatus(filterGroup);
  return COUNT_STATUS_VALUES.map((status) => ({
    viewKey: status,
    filterGroup: encodeSupportTicketCountFilterGroup(
      buildStatusVariantFilterGroup(baseFilterGroup, status),
    ),
  }));
}
